/**
 * @author superchow
 * @emil superchow@live.cn
 */
import { encodeBase64 } from 'pUtils'

import { guid, wrap, getBoundary } from './utils'
import forge from 'node-forge'
import quotedPrintable from 'quoted-printable'
import {
  EmailAddress,
  ParsedEmlJson,
  ReadedEmlJson,
  BuildOptions,
  CallbackFn,
  OptionOrNull,
  Options,
  SignatureEmlAttachment,
} from './interface'

/**
 * log for test
 */
let verbose: boolean = false
// const defaultCharset = 'utf-8'

/**
 * create a boundary
 */
function createBoundary(): string {
  return '----=' + guid()
}
/**
 * Builds e-mail address string, e.g. { name: 'PayPal', email: 'noreply@paypal.com' } => 'PayPal' <noreply@paypal.com>
 * @param {String|EmailAddress|EmailAddress[]|null} data
 */
function toEmailAddress(data?: string | EmailAddress | EmailAddress[] | null): string {
  let email = ''
  if (typeof data === 'undefined') {
    //No e-mail address
  } else if (typeof data === 'string') {
    email = data
  } else if (typeof data === 'object') {
    if (Array.isArray(data)) {
      email += data
        .map((item) => {
          let str = ''
          if (item.name) {
            str += '"' + item.name.replace(/^"|"\s*$/g, '') + '" '
          }
          if (item.email) {
            str += '<' + item.email + '>'
          }
          return str
        })
        .filter((a) => a)
        .join(', ')
    } else {
      if (data) {
        if (data.name) {
          email += '"' + data.name.replace(/^"|"\s*$/g, '') + '" '
        }
        if (data.email) {
          email += '<' + data.email + '>'
        }
      }
    }
  }
  return email
}

const blobToBase64 = async (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = (err) => reject(err)
    reader.readAsDataURL(blob)
  })
}

/**
 * buid EML file by ReadedEmlJson or EML file content
 * @param {ReadedEmlJson} data
 * @param {BuildOptions | CallbackFn<string> | null} options
 * @param {CallbackFn<string>} callback
 */
async function build(
  data: ReadedEmlJson,
  options?: BuildOptions | CallbackFn<string> | null,
  callback?: CallbackFn<string>,
): Promise<{ eml: string; error: Error | string | undefined }> {
  //Shift arguments
  if (typeof options === 'function' && typeof callback === 'undefined') {
    callback = options
    options = null
  }
  let error: Error | string | undefined
  let eml = ''
  const EOL = '\r\n' //End-of-line

  try {
    if (!data) {
      throw new Error('Argument "data" expected to be an object! or string')
    }

    if (!data.headers) {
      data.headers = {}
    }

    if (typeof data.subject === 'string') {
      data.headers['Subject'] = data.subject.normalize('NFC')
    }

    if (typeof data.from !== 'undefined') {
      data.headers['From'] = toEmailAddress(data.from)
    }

    if (typeof data.to !== 'undefined') {
      data.headers['To'] = toEmailAddress(data.to)
    }

    if (typeof data.cc !== 'undefined') {
      data.headers['Cc'] = toEmailAddress(data.cc)
    }

    // if (!data.headers['To']) {
    //   throw new Error('Missing "To" e-mail address!');
    // }

    let boundary = getBoundary(data.headers['Content-Type'])
    let hasBoundary = boundary != null
    let multipartBoundary = ''

    const hasAttachments = data.attachments && data.attachments.length > 0
    if (data.multipart === 'mixed') {
      boundary = createBoundary()
      multipartBoundary = boundary
      data.headers['Content-Type'] = 'multipart/mixed;' + EOL + 'boundary="' + boundary + '"'
      hasBoundary = true
    } else if (data.multipart === 'related' || hasAttachments) {
      boundary = createBoundary()
      data.headers['Content-Type'] = 'multipart/related;' + EOL + 'boundary="' + boundary + '"'
      // Restrained
      hasBoundary = true
    } else {
      hasBoundary = false
    }

    //Build headers
    const keys = Object.keys(data.headers)
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i]
      const value: string | string[] = data.headers[key]
      if (typeof value === 'undefined') {
        continue //Skip missing headers
      } else if (typeof value === 'string') {
        eml += key + ': ' + value.trim().replace(/\r?\n/g, EOL + '  ') + EOL
      } else {
        //Array
        for (let j = 0; j < value.length; j++) {
          eml += key + ': ' + value[j].trim().replace(/\r?\n/g, EOL + '  ') + EOL
        }
      }
    }

    if (data.multipartAlternative) {
      if (hasBoundary) {
        eml += EOL
        eml += '--' + boundary + EOL
      }

      multipartBoundary =
        '' + (getBoundary(data.multipartAlternative['Content-Type']) || createBoundary())

      eml +=
        'Content-Type: multipart/alternative;' +
        EOL +
        '  boundary="' +
        multipartBoundary +
        '"' +
        EOL
    }

    //Start the body

    //Plain text content
    if (data.text) {
      eml += EOL

      // Encode opened and self headers keeped
      if (
        hasBoundary &&
        typeof options === 'object' &&
        !!options &&
        options.encode &&
        data.textheaders
      ) {
        eml += '--' + boundary + EOL
        for (const key in data.textheaders) {
          if (data.textheaders.hasOwnProperty(key)) {
            eml += `${key}: ${data.textheaders[key].replace(/\r?\n/g, EOL + '  ')}`
          }
        }
      } else if (multipartBoundary) {
        // else Assembly
        eml += '--' + multipartBoundary + EOL
        eml += 'Content-Type: text/plain; charset="utf-8"' + EOL
        eml += 'Content-Transfer-Encoding: quoted-printable' + EOL
      }
      eml += EOL + quotedPrintable.encode(forge.util.encodeUtf8(data.text))
      eml += EOL
    }

    //HTML content
    if (data.html) {
      eml += EOL

      // Encode opened and self headers keeped
      if (
        hasBoundary &&
        typeof options === 'object' &&
        !!options &&
        options.encode &&
        data.textheaders
      ) {
        eml += '--' + boundary + EOL
        for (const key in data.textheaders) {
          if (data.textheaders.hasOwnProperty(key)) {
            eml += `${key}: ${data.textheaders[key].replace(/\r?\n/g, EOL + '  ')}`
          }
        }
      } else if (multipartBoundary) {
        eml += '--' + multipartBoundary + EOL
        eml += 'Content-Type: text/html; charset="utf-8"' + EOL
        eml += 'Content-Transfer-Encoding: quoted-printable' + EOL
      }
      if (verbose) {
        console.info(
          `line 765 ${hasBoundary}, multipartBoundary: ${multipartBoundary}, boundary: ${boundary}`,
        )
      }
      eml += EOL + quotedPrintable.encode(forge.util.encodeUtf8(data.html))
      eml += EOL
    }

    if (multipartBoundary && data.multipart !== 'mixed') {
      eml += EOL
      if (hasBoundary) {
        eml += '--' + multipartBoundary + EOL
      } else {
        eml += '--' + multipartBoundary + '--' + EOL
      }
    }

    //Append attachments
    if (data.attachments) {
      for (let i = 0; i < data.attachments.length; i++) {
        const attachment = data.attachments[i]
        const sizeStr = attachment.size != null ? '; size=' + attachment.size : ''
        eml += EOL
        eml += '--' + boundary + EOL
        eml +=
          'Content-Type: ' +
          (attachment.contentType.replace(/\r?\n/g, EOL + '  ') || 'application/octet-stream') +
          EOL
        eml += 'Content-Transfer-Encoding: base64' + EOL
        eml +=
          'Content-Disposition: ' +
          (attachment.inline ? 'inline' : 'attachment') +
          '; filename="' +
          (attachment.filename || attachment.name || 'attachment_' + (i + 1)) +
          '"' +
          sizeStr +
          EOL
        if (attachment.cid) {
          eml += 'Content-ID: <' + attachment.cid + '>' + EOL
        }
        eml += EOL
        if (typeof attachment.data === 'string') {
          const content = btoa(attachment.data)
          eml += wrap(content, 72) + EOL
        } else if (attachment.data instanceof Blob) {
          const content = await blobToBase64(attachment.data)
          const base64 = content.substring(content.indexOf(',') + 1)
          eml += wrap(base64, 72) + EOL
        } else {
          //Buffer
          // Uint8Array to string by new TextEncoder
          const content = btoa(
            String.fromCharCode.apply(null, new Uint8Array(attachment.data) as unknown as number[]),
          )
          eml += wrap(content, 72) + EOL
        }
      }
    }

    //Finish the boundary
    if (hasBoundary) {
      eml += EOL
      eml += '--' + boundary + '--' + EOL
    }
  } catch (e) {
    error = e as string
  }
  callback && callback(error, eml)
  return {
    eml,
    error,
  }
}

async function signatureAttachmentToBase64(
  data: Blob | string | ArrayBuffer | Uint8Array,
): Promise<string> {
  if (typeof data === 'string') {
    return btoa(data)
  }
  if (data instanceof Blob) {
    const dataUrl = await blobToBase64(data)
    return dataUrl.substring(dataUrl.indexOf(',') + 1)
  }
  return btoa(String.fromCharCode.apply(null, new Uint8Array(data) as unknown as number[]))
}

/**
 * 서명 저장 전용 EML 빌더. officemail-platform 의 build_eml_for_server 와 동일한 포맷을 생성한다.
 *
 * 공용 buildEml 은 서명 저장 경로(multipart/mixed)에서 본문을 quoted-printable 로 인코딩해
 * 한글(UTF-8 3byte)이 =XX=XX=XX(9자)로 ~3배 팽창한다. platform 은 본문을 raw UTF-8 로 저장하므로
 * 그에 맞춰 QP/base64 인코딩 없이 raw 로 넣어 크기·상호운용(platform 저장분 재저장)을 일치시킨다.
 * - 인라인 이미지가 없으면 단일 text/html 파트.
 * - 있으면 multipart/related + base64(cid) 파트. 본문 html 은 이미 data URI -> cid 로 치환된 상태.
 */
async function buildSignatureEml(
  html: string,
  attachments?: SignatureEmlAttachment[],
): Promise<{ eml: string; error: Error | string | undefined }> {
  const EOL = '\r\n'
  try {
    const inlineList = (attachments || []).filter((item) => item != null && item.data != null)

    if (inlineList.length === 0) {
      return {
        eml: `Content-Type: text/html; charset=utf-8${EOL}${EOL}${html}`,
        error: undefined,
      }
    }

    const boundary = createBoundary()
    let eml = `Content-Type: multipart/related; boundary="${boundary}"${EOL}${EOL}`
    eml += `--${boundary}${EOL}`
    eml += `Content-Type: text/html; charset=utf-8${EOL}${EOL}`
    eml += `${html}${EOL}`

    for (const attachment of inlineList) {
      eml += `--${boundary}${EOL}`
      eml += `Content-Type: ${attachment.contentType || 'application/octet-stream'}${EOL}`
      eml += `Content-Transfer-Encoding: base64${EOL}`
      if (attachment.filename) {
        eml += `Content-Disposition: ${attachment.inline ? 'inline' : 'attachment'}; filename="${attachment.filename}"${EOL}`
      }
      if (attachment.cid) {
        eml += `Content-ID: <${attachment.cid}>${EOL}`
      }
      eml += EOL
      eml += `${await signatureAttachmentToBase64(attachment.data)}${EOL}`
    }

    eml += `--${boundary}--${EOL}`
    return { eml, error: undefined }
  } catch (e) {
    return { eml: '', error: e as string }
  }
}

function base64utf8String(str: string) {
  if (str != null && str.length > 0) {
    const normalizedName = str.normalize('NFC')
    return `=?UTF-8?B?${encodeBase64(normalizedName)}?=`
  }
  return ''
}

/**
 * Parses EML file content and returns object-oriented representation of the content.
 * @param {String} eml
 * @param {OptionOrNull | CallbackFn<ParsedEmlJson>} options
 * @param {CallbackFn<ParsedEmlJson>} callback
 * @returns {string | Error | ParsedEmlJson}
 */
function parse(
  eml: string,
  options?: OptionOrNull | CallbackFn<ParsedEmlJson>,
  callback?: CallbackFn<ParsedEmlJson>,
): string | Error | ParsedEmlJson {
  //Shift arguments
  if (typeof options === 'function' && typeof callback === 'undefined') {
    callback = options
    options = null
  }
  if (typeof options !== 'object') {
    options = { headersOnly: false }
  }
  let error: string | Error | undefined
  let result: ParsedEmlJson | undefined = {} as ParsedEmlJson
  try {
    if (typeof eml !== 'string') {
      throw new Error('Argument "eml" expected to be string!')
    }

    const lines = eml.split(/\r?\n/)
    result = parseRecursive(lines, 0, result, options as Options) as ParsedEmlJson
  } catch (e) {
    error = e as string
  }
  callback && callback(error, result)
  return error || result || new Error('read EML failed!')
}

/**
 * Parses EML file content.
 * @param {String[]} lines
 * @param {Number}   start
 * @param {Options}  options
 * @returns {ParsedEmlJson}
 */
function parseRecursive(lines: string[], start: number, parent: any, options: Options) {
  let boundary: any = null
  let lastHeaderName = ''
  let findBoundary = ''
  let insideBody = false
  let insideBoundary = false
  let isMultiHeader = false
  let isMultipart = false
  let checkedForCt = false
  let ctInBody = false

  parent.headers = {}
  //parent.body = null;

  function complete(boundary: any) {
    //boundary.part = boundary.lines.join("\r\n");
    boundary.part = {}
    parseRecursive(boundary.lines, 0, boundary.part, options)
    delete boundary.lines
  }

  //Read line by line
  for (let i = start; i < lines.length; i++) {
    let line = lines[i]

    //Header
    if (!insideBody) {
      //Search for empty line
      if (line == '') {
        insideBody = true

        if (options && options.headersOnly) {
          break
        }

        //Expected boundary
        let ct = parent.headers['Content-Type'] || parent.headers['Content-type']
        if (!ct) {
          if (checkedForCt) {
            insideBody = !ctInBody
          } else {
            checkedForCt = true
            const lineClone = Array.from(lines)
            const string = lineClone.splice(i).join('\r\n')
            const trimmedStrin = string.trim()
            if (
              trimmedStrin.indexOf('Content-Type') === 0 ||
              trimmedStrin.indexOf('Content-type') === 0
            ) {
              insideBody = false
              ctInBody = true
            } else {
              console.warn('Warning: undefined Content-Type')
            }
          }
        } else if (/^multipart\//g.test(ct)) {
          let b = getBoundary(ct)
          if (b && b.length) {
            findBoundary = b
            isMultipart = true
            parent.body = []
          } else {
            if (verbose) {
              console.warn('Multipart without boundary! ' + ct.replace(/\r?\n/g, ' '))
            }
          }
        }

        continue
      }

      //Header value with new line
      let match = /^\s+([^\r\n]+)/g.exec(line)
      if (match) {
        if (isMultiHeader) {
          parent.headers[lastHeaderName][parent.headers[lastHeaderName].length - 1] +=
            '\r\n' + match[1]
        } else {
          parent.headers[lastHeaderName] += '\r\n' + match[1]
        }
        continue
      }

      //Header name and value
      match = /^([\w\d\-]+):\s*([^\r\n]*)/gi.exec(line)
      if (match) {
        lastHeaderName = match[1]
        if (parent.headers[lastHeaderName]) {
          //Multiple headers with the same name
          isMultiHeader = true
          if (typeof parent.headers[lastHeaderName] == 'string') {
            parent.headers[lastHeaderName] = [parent.headers[lastHeaderName]]
          }
          parent.headers[lastHeaderName].push(match[2])
        } else {
          //Header first appeared here
          isMultiHeader = false
          parent.headers[lastHeaderName] = match[2]
        }
        continue
      }
    }
    //Body
    else {
      //Multipart body
      if (isMultipart) {
        //Search for boundary start

        //Updated on 2019-10-12: A line before the boundary marker is not required to be an empty line
        //if (lines[i - 1] == "" && line.indexOf("--" + findBoundary) == 0 && !/\-\-(\r?\n)?$/g.test(line)) {
        if (
          line.indexOf('--' + findBoundary) == 0 &&
          line.indexOf('--' + findBoundary + '--') !== 0
        ) {
          insideBoundary = true

          //Complete the previous boundary
          if (boundary && boundary.lines) {
            complete(boundary)
          }

          //Start a new boundary
          let match = /^\-\-([^\r\n]+)(\r?\n)?$/g.exec(line) as RegExpExecArray
          boundary = { boundary: match[1], lines: [] as any[] }
          parent.body.push(boundary)

          if (verbose) {
            console.log('Found boundary: ' + boundary.boundary)
          }

          continue
        }

        if (insideBoundary) {
          //Search for boundary end
          if (
            boundary?.boundary &&
            lines[i - 1] == '' &&
            line.indexOf('--' + findBoundary + '--') == 0
          ) {
            insideBoundary = false
            complete(boundary)
            continue
          }
          if (boundary?.boundary && line.indexOf('--' + findBoundary + '--') == 0) {
            continue
          }
          boundary?.lines.push(line)
        }
      } else {
        //Solid string body
        parent.body = lines.splice(i).join('\r\n')
        break
      }
    }
  }

  //Complete the last boundary
  if (parent.body && parent.body.length && parent.body[parent.body.length - 1].lines) {
    complete(parent.body[parent.body.length - 1])
  }

  return parent
}

/**
 * if you need
 * eml-format all api
 */
export {
  toEmailAddress,
  createBoundary,
  getBoundary,
  parse as parseEml,
  build as buildEml,
  buildSignatureEml,
  base64utf8String,
}
